import { useState } from "react";
import { flushSync } from "react-dom";
import { api, STATUS } from "@/lib/api";
import { toast } from "sonner";
import { SEM_TROCO_FORMAS } from "@/pages/entregas/entregasHelpers";
import { getGPSAsync } from "@/lib/geoAsync";

const FINAIS = ["concluida", "remarcar", "cancelada"];
const MSG_GPS = "Habilite a localização (GPS) do seu celular para finalizar esta entrega";

// Fluxo de mudança de status: foto de evidência (se exigida) → prompt (código 4 dígitos / troco /
// motivo) → PUT /status com GPS. Updates otimistas mantêm a ordem dos cards estável enquanto os
// Dialogs animam; o reload acontece só depois (`runAfterDialogClose`).
export function useStatusFlow({ data, flow }) {
  const { setItems, cfg, formas, formasByEmpresa, lookup, load } = data;
  const { flowBusy, setFlowBusy, clearFlowBusy, showFlowSuccess, runAfterDialogClose } = flow;
  const [prompt, setPromptRaw] = useState(null);
  const [evidencePending, setEvidencePending] = useState(null);
  const [saving, setSaving] = useState(false);  // trava dupla submissão

  const optimisticUpdate = (id, patch) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const afterSuccess = (id, status, gps) => {
    optimisticUpdate(id, { status, latitude: gps.latitude, longitude: gps.longitude });
    window.dispatchEvent(new Event("entregas-changed"));
  };

  const changeFormaPgto = async (it, formaId) => {
    const prev = it.forma_pgto_id;
    optimisticUpdate(it.id, { forma_pgto_id: formaId });
    try {
      await api.put(`/minhas-entregas/${it.id}/forma-pgto`, { forma_pgto_id: formaId });
      toast.success(`Pagamento: ${formaId ? lookup.fpg[formaId] : "—"}`);
    } catch (e) {
      optimisticUpdate(it.id, { forma_pgto_id: prev });
      toast.error(e?.response?.data?.detail || "Erro ao alterar forma de pagamento");
    }
  };

  const commitStatus = async (it, status, extra = {}) => {
    if (saving) return;
    setSaving(true);
    setFlowBusy({ entregaId: it.id, status, phase: "pending" });
    const gps = await getGPSAsync();
    if (cfg.require_gps_capture && FINAIS.includes(status) && (gps.latitude == null || gps.longitude == null)) {
      toast.error(MSG_GPS);
      clearFlowBusy();
      setSaving(false);
      return;
    }
    try {
      await api.put(`/minhas-entregas/${it.id}/status`, { status, ...gps, ...extra });
      toast.success(`Status: ${STATUS[status]}`);
      afterSuccess(it.id, status, gps);
      showFlowSuccess(it.id, status, () => runAfterDialogClose(load));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
      clearFlowBusy();
    } finally {
      setSaving(false);
    }
  };

  const buildConcluirPrompt = (it, status) => {
    const formaNome = ((formasByEmpresa[it.empresa_id] || []).find((f) => f.id === it.forma_pgto_id) || formas.find((f) => f.id === it.forma_pgto_id))?.nome || it.forma_pgto_nome || "";
    // forma desconhecida (catálogo não carregou) → só exige troco se o backend calculou troco > 0
    const dinheiro = formaNome ? !SEM_TROCO_FORMAS.some((s) => formaNome.toLowerCase().includes(s)) : (Number(it.troco) || 0) > 0;
    const valor = Number(it.valor) || 0;
    const trocoPara = Number(it.troco_para) || 0;
    const needTroco = dinheiro && valor > 0;
    const troco = needTroco ? Math.max(0, (trocoPara > 0 ? trocoPara : valor) - valor) : 0;
    const need4dig = !!(cfg.require_codigo_4dig && it.cliente_telefone);
    return { entrega: it, status, field: "concluir", value: "", obs: "", trocoOk: false,
      need4dig, needTroco, dinheiro, valor, trocoPara, troco, formaNome,
      label: need4dig
        ? `Confirme os 4 últimos dígitos do celular do cliente (${it.cliente_nome || "destinatário"})`
        : `Entrega para ${it.cliente_nome || "o cliente"}` };
  };

  const openPromptAfterEvidence = (it, status) => {
    setPromptRaw(status === "concluida"
      ? buildConcluirPrompt(it, status)
      : { entrega: it, status, field: "motivo", value: "", label: "Motivo * (obrigatório)" });
  };

  const setStatus = async (it, status) => {
    if (flowBusy || saving) return;  // trava dupla-clique
    if (FINAIS.includes(status)) {
      setFlowBusy({ entregaId: it.id, status, phase: "pending" });
      if (cfg.require_foto_evidencia && !it.foto_evidencia_path) setEvidencePending({ entrega: it, status });
      else openPromptAfterEvidence(it, status);
      return;
    }
    await commitStatus(it, status);
  };

  const validatePrompt = (p) => {
    const { status, field, value } = p;
    if ((status === "cancelada" || status === "remarcar") && field === "motivo" && !String(value || "").trim()) return "Motivo é obrigatório para cancelar ou remarcar";
    if (field === "concluir") {
      if (p.need4dig && String(value || "").replace(/\D/g, "").length !== 4) return "Digite exatamente 4 dígitos";
      if (p.needTroco && !p.trocoOk) return p.troco > 0 ? "Confirme o troco antes de concluir" : "Confirme o valor recebido antes de concluir";
    }
    return null;
  };

  const buildPromptPayload = (p, gps) => {
    const { status, field, value } = p;
    const payload = { status, ...gps };
    if (field !== "concluir") { payload[field] = value; return payload; }
    if (p.need4dig) payload.confirmacao_4dig = String(value || "").replace(/\D/g, "");
    if (p.needTroco) { payload.troco_confirmado = true; payload.valor_recebido = p.trocoPara > 0 ? p.trocoPara : p.valor; }
    else if (p.valor > 0) payload.valor_recebido = p.valor;
    if (String(p.obs || "").trim()) payload.observacao_entregador = String(p.obs).trim();
    return payload;
  };

  const confirmPrompt = async (overrides = null) => {
    if (saving) return;
    const p = overrides && typeof overrides === "object" && !overrides.nativeEvent ? { ...prompt, ...overrides } : prompt;
    const erro = validatePrompt(p);
    if (erro) return toast.error(erro);
    setSaving(true);
    toast.loading("Capturando localização GPS...", { id: "gps" });
    const gps = await getGPSAsync();
    toast.dismiss("gps");
    if (cfg.require_gps_capture && (gps.latitude == null || gps.longitude == null)) {
      setSaving(false);
      return toast.error(MSG_GPS);
    }
    const { entrega, status } = p;
    try {
      await api.put(`/minhas-entregas/${entrega.id}/status`, buildPromptPayload(p, gps));
      toast.success(`Entrega ${STATUS[status].toLowerCase()}${gps.latitude ? " · GPS registrado" : ""}`);
      afterSuccess(entrega.id, status, gps);
      showFlowSuccess(entrega.id, status, () => {
        flushSync(() => { setPromptRaw(null); });
        runAfterDialogClose(load);
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
      clearFlowBusy();
    } finally {
      setSaving(false);
    }
  };

  const setPrompt = (v) => { if (!v) clearFlowBusy(); setPromptRaw(v); };
  const closeEvidence = () => { setEvidencePending(null); clearFlowBusy(); };
  const onEvidenceUploaded = () => {
    const { entrega, status } = evidencePending || {};
    setEvidencePending(null);
    if (!entrega || !status) return;
    // marca a foto como enviada: o próximo clique pula direto para o prompt sem reabrir a câmera
    optimisticUpdate(entrega.id, { foto_evidencia_path: `local:${entrega.id}` });
    openPromptAfterEvidence(entrega, status);
  };

  return { prompt, setPrompt, evidencePending, closeEvidence, onEvidenceUploaded, saving, setStatus, confirmPrompt, changeFormaPgto };
}
